import type {
  Team,
  TeamsQueryParams,
  TeamsResponse,
  TeamPayload,
  UserLookupResponse,
  ValidationResponse,
  HealthResponse,
  Manager,
  Developer
} from '../../types/task-sentinel'
import { apiClient } from '../api/apiClient'
import { logger } from '../../utils/logger'

const API_BASE = '/platform/task-sentinel/clickup'

/**
 * Manager type display order
 */
const MANAGER_TYPE_ORDER: Record<string, number> = {
  DIRECTOR: 1,
  EM: 2,
  TL: 3,
  PM: 4,
  GPM: 5
}

export const taskSentinelService = {
  /**
   * Fetch paginated list of teams
   */
  async fetchTeams(params: TeamsQueryParams = {}): Promise<Team[]> {
    try {
      const queryParams: Record<string, string> = {}
      if (params.offset !== undefined) queryParams.offset = params.offset.toString()
      if (params.limit !== undefined) queryParams.limit = params.limit.toString()

      logger.log('Fetching teams with params:', queryParams)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await apiClient.get<any>(`${API_BASE}/teams/`, queryParams)

      // Handle different response structures
      let teams: Team[] = []
      if (Array.isArray(response)) {
        teams = response
      } else if (response?.data && Array.isArray(response.data)) {
        teams = response.data
      } else if (response?.success && response?.data) {
        teams = Array.isArray(response.data) ? response.data : []
      }

      logger.log('Teams fetched:', teams.length)
      return teams
    } catch (error) {
      logger.error('Failed to fetch teams:', error)
      throw new Error('Failed to fetch teams. Please try again.')
    }
  },

  /**
   * Fetch single team by ID
   */
  async fetchTeamById(id: string): Promise<Team> {
    try {
      logger.log('Fetching team by ID:', id)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await apiClient.get<any>(`${API_BASE}/teams/${id}`)

      // Handle different response structures
      let team: Team
      if (response?.data && typeof response.data === 'object') {
        team = response.data
      } else if (response?.id) {
        // Response is the team object directly
        team = response
      } else {
        team = response
      }

      logger.log('Team fetched:', { id: team?.id, name: team?.name })
      return team
    } catch (error) {
      logger.error('Failed to fetch team:', error)
      throw new Error('Failed to fetch team details. Please try again.')
    }
  },

  /**
   * Create a new team
   */
  async createTeam(id: string, team: TeamPayload): Promise<Team> {
    try {
      logger.log('Creating team:', { id, name: team.name })

      const response = await apiClient.post<TeamsResponse>(`${API_BASE}/teams/${id}`, team)

      if (!response.success) {
        throw new Error(response.message || 'Failed to create team')
      }

      logger.log('Team created successfully')
      return response.data as unknown as Team
    } catch (error) {
      logger.error('Failed to create team:', error)
      throw error instanceof Error ? error : new Error('Failed to create team. Please try again.')
    }
  },

  /**
   * Update an existing team
   */
  async updateTeam(id: string, team: TeamPayload): Promise<Team> {
    try {
      logger.log('Updating team:', { id, name: team.name })

      const response = await apiClient.put<TeamsResponse>(`${API_BASE}/teams/${id}`, team)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resp = response as any
      if (resp?.success === false) {
        throw new Error(resp.message || 'Failed to update team')
      }

      logger.log('Team updated successfully')
      return resp.data || resp
    } catch (error) {
      logger.error('Failed to update team:', error)
      throw error instanceof Error ? error : new Error('Failed to update team. Please try again.')
    }
  },

  /**
   * Validate team changes and get diff
   */
  async validateTeam(id: string, team: TeamPayload): Promise<ValidationResponse> {
    try {
      logger.log('Validating team changes:', { id })

      const response = await apiClient.post<ValidationResponse>(
        `${API_BASE}/teams/validate?id=${id}`,
        team
      )

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resp = response as any
      if (resp?.success === false) {
        throw new Error(resp.message || 'Validation failed')
      }

      logger.log('Validation successful')
      return resp
    } catch (error) {
      logger.error('Failed to validate team:', error)
      throw error instanceof Error
        ? error
        : new Error('Failed to validate changes. Please try again.')
    }
  },

  /**
   * Lookup user by email
   */
  async lookupUserByEmail(email: string): Promise<UserLookupResponse> {
    try {
      logger.log('Looking up user by email:', email)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await apiClient.get<any>(`${API_BASE}/users/by-email`, { email })

      // The apiClient unwraps nested data, so response might be:
      // 1. The full response { success, data } if not unwrapped
      // 2. Just the data object { email, slackId, name } if unwrapped
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resp = response as any

      // Check if response was unwrapped (has email directly) or not (has success/data)
      if (resp?.email) {
        // Response was unwrapped - wrap it back
        logger.log('User lookup result:', { found: true, email })
        return { success: true, data: resp }
      } else if (resp?.success && resp?.data) {
        // Response was not unwrapped
        logger.log('User lookup result:', { found: true, email })
        return resp
      } else {
        logger.log('User lookup result:', { found: false, email })
        return { success: false, message: 'User not found' }
      }
    } catch (error) {
      logger.error('Failed to lookup user:', error)
      return { success: false, message: 'User not found' }
    }
  },

  /**
   * Check API health
   */
  async checkHealth(): Promise<boolean> {
    try {
      const response = await apiClient.get<HealthResponse>(`${API_BASE}/teams/health`)
      return true
    } catch (error) {
      logger.error('Health check failed:', error)
      return false
    }
  },

  /**
   * Generate a random ID for new teams (20 characters)
   */
  generateTeamId(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < 20; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  },

  /**
   * Get manager emails by type
   */
  getManagerEmails(managers: Manager[] | undefined, type: string): string {
    if (!managers || !Array.isArray(managers)) return '-'
    const filtered = managers.filter((m) => m.type === type)
    if (filtered.length === 0) return '-'
    return filtered.map((m) => m.emailPrefix || '').join(', ')
  },

  /**
   * Get PM list (PM and GPM types)
   */
  getPMList(managers: Manager[] | undefined): string {
    if (!managers || !Array.isArray(managers)) return '-'
    const pms = managers.filter((m) => m.type === 'PM' || m.type === 'GPM')
    if (pms.length === 0) return '-'
    return pms.map((m) => m.emailPrefix || '').join(', ')
  },

  /**
   * Get developer emails
   */
  getDeveloperEmails(devs: Developer[] | undefined): string {
    if (!devs || !Array.isArray(devs)) return '-'
    if (devs.length === 0) return '-'
    return devs.map((d) => d.emailPrefix || '').join(', ')
  },

  /**
   * Sort managers by type order
   */
  sortManagersByType(managers: Manager[]): Manager[] {
    return [...managers].sort((a, b) => {
      const aOrder = MANAGER_TYPE_ORDER[a.type?.toUpperCase()] || 99
      const bOrder = MANAGER_TYPE_ORDER[b.type?.toUpperCase()] || 99
      return aOrder - bOrder
    })
  },

  /**
   * Format date for display
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  },

  /**
   * Get status badge color classes
   */
  getStatusColor(deleted: boolean): string {
    return deleted
      ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
      : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
  },

  /**
   * Get status text
   */
  getStatusText(deleted: boolean): string {
    return deleted ? 'Deleted' : 'Active'
  },

  /**
   * Extract email prefix from full email
   */
  extractEmailPrefix(email: string): string {
    return email.split('@')[0]
  },

  /**
   * Compute line-by-line diff using LCS algorithm
   */
  computeLineDiff(
    oldLines: string[],
    newLines: string[]
  ): Array<{
    type: 'equal' | 'delete' | 'add'
    oldLine: string | null
    newLine: string | null
    oldNum: number | null
    newNum: number | null
  }> {
    const m = oldLines.length
    const n = newLines.length

    // Build LCS table using dynamic programming
    const dp: number[][] = Array(m + 1)
      .fill(null)
      .map(() => Array(n + 1).fill(0))

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (oldLines[i - 1] === newLines[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1])
        }
      }
    }

    // Backtrack to find the diff
    const result: Array<{
      type: 'equal' | 'delete' | 'add'
      oldLine: string | null
      newLine: string | null
      oldNum: number | null
      newNum: number | null
    }> = []

    let i = m
    let j = n

    while (i > 0 || j > 0) {
      if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
        result.unshift({
          type: 'equal',
          oldLine: oldLines[i - 1],
          newLine: newLines[j - 1],
          oldNum: i,
          newNum: j
        })
        i--
        j--
      } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
        result.unshift({
          type: 'add',
          oldLine: null,
          newLine: newLines[j - 1],
          oldNum: null,
          newNum: j
        })
        j--
      } else if (i > 0) {
        result.unshift({
          type: 'delete',
          oldLine: oldLines[i - 1],
          newLine: null,
          oldNum: i,
          newNum: null
        })
        i--
      }
    }

    return result
  },

  /**
   * Sort JSON keys recursively for consistent comparison
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortJsonKeys(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.sortJsonKeys(item))
    }

    if (typeof obj !== 'object') {
      return obj
    }

    const sorted: Record<string, unknown> = {}
    const keys = Object.keys(obj).sort()
    for (const key of keys) {
      sorted[key] = this.sortJsonKeys(obj[key])
    }
    return sorted
  }
}
